import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, Section } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div>Loading…</div>;

  const loadPreview = async () => {
    setPreviewing(true);
    try { const r = await api.get("/approvals/reminder-preview"); setPreview(r.data); }
    catch (e) { toast.error(formatErr(e)); }
    setPreviewing(false);
  };

  const save = async () => {
    try { await api.put("/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const setLeave = (k, v) => setS({ ...s, leave_allocations: { ...s.leave_allocations, [k]: parseFloat(v) || 0 } });

  return (
    <div data-testid="admin-settings">
      <PageHeader overline="Configuration" title="Company Settings" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Working Hours & Overtime">
          <div className="space-y-3">
            <div><div className="eyebrow mb-1">Daily hours</div><Input type="number" step="0.5" value={s.daily_hours} onChange={(e) => setS({ ...s, daily_hours: parseFloat(e.target.value) || 0 })} className="rounded-none" data-testid="set-daily" /></div>
            <div><div className="eyebrow mb-1">Weekly hours</div><Input type="number" step="0.5" value={s.weekly_hours} onChange={(e) => setS({ ...s, weekly_hours: parseFloat(e.target.value) || 0 })} className="rounded-none" /></div>
            <div><div className="eyebrow mb-1">Overtime threshold (per day)</div><Input type="number" step="0.5" value={s.overtime_threshold} onChange={(e) => setS({ ...s, overtime_threshold: parseFloat(e.target.value) || 0 })} className="rounded-none" data-testid="set-ot-threshold" /></div>
            <div><div className="eyebrow mb-1">Grace period (min)</div><Input type="number" value={s.grace_period_minutes} onChange={(e) => setS({ ...s, grace_period_minutes: parseInt(e.target.value) || 0 })} className="rounded-none" /></div>
            <div><div className="eyebrow mb-1">Working days (comma-separated: Mon,Tue,...)</div>
              <Input value={(s.working_days || []).join(",")} onChange={(e) => setS({ ...s, working_days: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="rounded-none" data-testid="set-working-days" placeholder="Mon,Tue,Wed,Thu,Fri" />
              <div className="text-xs text-neutral-500 mt-1">Days not listed here are treated as weekend.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><div className="eyebrow mb-1">Weekend multiplier</div><Input type="number" step="0.1" value={s.weekend_multiplier ?? 2.0} onChange={(e) => setS({ ...s, weekend_multiplier: parseFloat(e.target.value) || 0 })} className="rounded-none" data-testid="set-weekend-mult" /></div>
              <div><div className="eyebrow mb-1">Holiday multiplier</div><Input type="number" step="0.1" value={s.holiday_multiplier ?? 2.0} onChange={(e) => setS({ ...s, holiday_multiplier: parseFloat(e.target.value) || 0 })} className="rounded-none" data-testid="set-holiday-mult" /></div>
            </div>
            <div><div className="eyebrow mb-1">Timesheet entry method</div>
              <select value={s.entry_method} onChange={(e) => setS({ ...s, entry_method: e.target.value })} className="w-full h-10 border border-[#E5E5E5] px-2 bg-white" data-testid="set-entry-method">
                <option value="start_end">Start/End times</option>
                <option value="direct">Direct hours</option>
                <option value="both">Both</option>
              </select>
            </div>
          </div>
        </Section>
        <Section title="Compensatory Off">
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!s.comp_off_enabled} onChange={(e) => setS({ ...s, comp_off_enabled: e.target.checked })} className="accent-[#FF5500]" data-testid="set-comp-off-enabled" />
              Grant comp-off when employees work on weekends or holidays
            </label>
            <div>
              <div className="eyebrow mb-1">Minimum hours for 1 comp-off day</div>
              <Input type="number" step="0.5" value={s.comp_off_min_hours ?? 8} onChange={(e) => setS({ ...s, comp_off_min_hours: parseFloat(e.target.value) || 0 })} className="rounded-none w-40" data-testid="set-comp-off-min" />
              <div className="text-xs text-neutral-500 mt-1">A day on a weekend/holiday with at least this many logged hours earns 1 comp-off (banked on week approval).</div>
            </div>
          </div>
        </Section>
        <Section title="Approval Reminders">
          <div className="space-y-3">
            <div>
              <div className="eyebrow mb-1">Reminder frequency</div>
              <select value={s.reminder_frequency || "daily"} onChange={(e) => setS({ ...s, reminder_frequency: e.target.value })}
                className="w-full h-10 border border-[#E5E5E5] px-2 bg-white" data-testid="set-reminder-frequency">
                <option value="daily">Daily</option>
                <option value="every_2_days">Every 2 days</option>
                <option value="weekly">Weekly</option>
              </select>
              <div className="text-xs text-neutral-500 mt-1">How often approvers get a digest of requests awaiting them.</div>
            </div>
            <div>
              <div className="eyebrow mb-1">Staleness threshold (days)</div>
              <Input type="number" min="1" step="1" value={s.reminder_staleness_days ?? 2}
                onChange={(e) => setS({ ...s, reminder_staleness_days: parseInt(e.target.value) || 1 })}
                className="rounded-none w-40" data-testid="set-reminder-staleness" />
              <div className="text-xs text-neutral-500 mt-1">Only requests waiting longer than this are included in the reminder.</div>
            </div>
            <div>
              <div className="eyebrow mb-1">Preferred send time</div>
              <Input type="time" value={s.reminder_send_time || "10:00"}
                onChange={(e) => setS({ ...s, reminder_send_time: e.target.value })}
                className="rounded-none w-40" data-testid="set-reminder-time" />
              <div className="text-xs text-neutral-500 mt-1">Reminders run daily; the frequency setting above controls how often an actual digest is sent.</div>
            </div>
            <div className="pt-1">
              <Button onClick={loadPreview} disabled={previewing} data-testid="preview-reminder-btn"
                className="rounded-none bg-[#1F4E8C] hover:bg-[#163a68] text-white">
                {previewing ? "Checking…" : "Preview recipients"}
              </Button>
            </div>
            {preview && (
              <div className="border border-[#E5E5E5] bg-[#F7F7F5] mt-1" data-testid="reminder-preview">
                <div className="px-3 py-2 border-b border-[#E5E5E5] text-xs">
                  {preview.would_send ? (
                    <span className="text-emerald-700 font-semibold">The next scheduled run will send.</span>
                  ) : (
                    <span className="text-amber-700 font-semibold">
                      Frequency guard active — next eligible{preview.next_eligible_at ? ` ${new Date(preview.next_eligible_at).toLocaleString()}` : ""}.
                    </span>
                  )}
                  <span className="text-neutral-500"> · {preview.total_recipients} recipient(s) · {preview.total_requests} request(s) older than {preview.staleness_days} day(s)</span>
                </div>
                {preview.recipients.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-neutral-500">No approvers have stale requests right now — nothing would be sent.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-[#E5E5E5]">
                        <th className="px-3 py-2 font-semibold">Approver</th>
                        <th className="px-3 py-2 font-semibold text-center">Leave</th>
                        <th className="px-3 py-2 font-semibold text-center">Timesheets</th>
                        <th className="px-3 py-2 font-semibold text-center">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.recipients.map((r) => (
                        <tr key={r.email} className="border-b border-[#F0F0EE]" data-testid={`preview-row-${r.email}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium">{r.name}</div>
                            <div className="text-xs text-neutral-500">{r.email}</div>
                          </td>
                          <td className="px-3 py-2 text-center">{r.leave}</td>
                          <td className="px-3 py-2 text-center">{r.timesheets}</td>
                          <td className="px-3 py-2 text-center font-semibold">{r.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </Section>
        <Section title="Leave Allocations (per year)">
          <div className="space-y-3">
            {Object.entries(s.leave_allocations || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3">
                <div className="flex-1 text-sm font-medium">{k}</div>
                <Input type="number" value={v} onChange={(e) => setLeave(k, e.target.value)} className="rounded-none w-32" data-testid={`leave-alloc-${k}`} />
              </div>
            ))}
            <div className="text-xs text-neutral-500">Comp Off balance is calculated automatically from approved weekend/holiday work and is not editable here.</div>
          </div>
        </Section>
      </div>
      <div className="mt-4"><Button onClick={save} className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white px-8" data-testid="save-settings">Save Settings</Button></div>
    </div>
  );
}
